import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { X, RotateCcw, Clock, Trash2, Eye, GitCompare, Plus, Minus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { deleteVersion, getPromptVersion, getVersionDiff } from '../../lib/api';
import { toast } from 'sonner';
import ReactDiffViewer from 'react-diff-viewer-continued';

export default function VersionHistory({ 
  versions, 
  currentVersion, 
  promptId,
  onRestore, 
  onClose,
  onVersionDeleted 
}) {
  const [viewingVersion, setViewingVersion] = useState(null);
  const [versionContent, setVersionContent] = useState(null);
  const [versionDiff, setVersionDiff] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [activeViewTab, setActiveViewTab] = useState('content');

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const handleViewVersion = async (version) => {
    setLoadingContent(true);
    setLoadingDiff(true);
    setActiveViewTab('content');
    try {
      const [contentRes, diffRes] = await Promise.all([
        getPromptVersion(promptId, version.version),
        getVersionDiff(promptId, version.version)
      ]);
      setVersionContent(contentRes.data);
      setVersionDiff(diffRes.data);
      setViewingVersion(version.version);
    } catch (err) {
      toast.error('Failed to load version content');
    } finally {
      setLoadingContent(false);
      setLoadingDiff(false);
    }
  };

  // Custom diff viewer styles for dark theme
  const diffViewerStyles = {
    variables: {
      dark: {
        diffViewerBackground: '#030712',
        diffViewerColor: '#F1F5F9',
        addedBackground: 'rgba(34, 197, 94, 0.15)',
        addedColor: '#4ade80',
        removedBackground: 'rgba(239, 68, 68, 0.15)',
        removedColor: '#f87171',
        wordAddedBackground: 'rgba(34, 197, 94, 0.3)',
        wordRemovedBackground: 'rgba(239, 68, 68, 0.3)',
        addedGutterBackground: 'rgba(34, 197, 94, 0.2)',
        removedGutterBackground: 'rgba(239, 68, 68, 0.2)',
        gutterBackground: '#0F172A',
        gutterBackgroundDark: '#0F172A',
        highlightBackground: 'rgba(14, 165, 233, 0.1)',
        highlightGutterBackground: 'rgba(14, 165, 233, 0.2)',
        codeFoldGutterBackground: '#1E293B',
        codeFoldBackground: '#1E293B',
        emptyLineBackground: '#0F172A',
        gutterColor: '#64748B',
        addedGutterColor: '#4ade80',
        removedGutterColor: '#f87171',
        codeFoldContentColor: '#94A3B8',
        diffViewerTitleBackground: '#1E293B',
        diffViewerTitleColor: '#F1F5F9',
        diffViewerTitleBorderColor: '#334155'
      }
    },
    line: {
      padding: '4px 8px',
      fontSize: '12px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
    },
    gutter: {
      padding: '0 8px',
      minWidth: '40px',
      fontSize: '11px'
    },
    contentText: {
      fontSize: '12px',
      lineHeight: '1.5'
    }
  };

  const handleDeleteVersion = async (versionNum) => {
    if (versionNum === currentVersion) {
      toast.error('Cannot delete current version');
      return;
    }
    
    setDeleting(versionNum);
    try {
      await deleteVersion(promptId, versionNum);
      toast.success(`Version ${versionNum} deleted`);
      onVersionDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete version');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="w-80 bg-[#0F172A] flex flex-col" data-testid="version-history-panel">
      {/* Header */}
      <div className="p-3 border-b border-[#1E293B] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#0EA5E9]" />
          <h3 className="text-sm font-medium text-[#F1F5F9]">Version History</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#1E293B] rounded-sm transition-colors"
          data-testid="close-history-btn"
        >
          <X className="w-4 h-4 text-[#64748B]" />
        </button>
      </div>

      {/* Version List */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {versions.length === 0 ? (
            <div className="p-4 text-center text-[#64748B] text-xs">
              No version history
            </div>
          ) : (
            versions.map((version) => (
              <div
                key={version.id}
                className={cn(
                  "relative pl-6 py-3 border-l-2 ml-2",
                  version.version === currentVersion
                    ? "border-[#0EA5E9]"
                    : "border-[#1E293B]"
                )}
                data-testid={`version-item-${version.version}`}
              >
                {/* Timeline dot */}
                <div
                  className={cn(
                    "absolute left-[-5px] top-4 w-2 h-2 rounded-full",
                    version.version === currentVersion
                      ? "bg-[#0EA5E9] shadow-[0_0_10px_rgba(14,165,233,0.5)]"
                      : "bg-[#334155]"
                  )}
                />

                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] font-mono",
                        version.version === currentVersion
                          ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/30"
                          : "text-[#94A3B8]"
                      )}
                    >
                      v{version.version}
                    </Badge>
                    {version.version === currentVersion && (
                      <span className="text-[10px] text-[#10B981]">Current</span>
                    )}
                  </div>
                  
                  <p className="text-xs text-[#94A3B8] line-clamp-2">
                    {version.change_note || 'No description'}
                  </p>
                  
                  <div className="text-[10px] text-[#64748B]">
                    {formatDate(version.created_at)}
                  </div>
                  
                  <div className="flex items-center gap-1 mt-2">
                    {/* View Version Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewVersion(version)}
                          className="h-6 px-2 text-[10px] text-[#94A3B8] hover:text-white"
                          data-testid={`view-version-${version.version}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[85vh] bg-[#0F172A] border-[#1E293B]">
                        <DialogHeader>
                          <DialogTitle className="text-[#F1F5F9] flex items-center gap-2">
                            Version {viewingVersion}
                            {versionDiff?.has_previous && (
                              <Badge variant="outline" className="text-[10px]">
                                vs v{versionDiff.previous_version}
                              </Badge>
                            )}
                          </DialogTitle>
                        </DialogHeader>
                        
                        <Tabs value={activeViewTab} onValueChange={setActiveViewTab} className="flex-1 flex flex-col">
                          <TabsList className="bg-transparent border-b border-[#1E293B] rounded-none w-full justify-start">
                            <TabsTrigger 
                              value="content" 
                              className="data-[state=active]:bg-[#1E293B] data-[state=active]:text-white"
                            >
                              <Eye className="w-4 h-4 mr-1.5" />
                              Content
                            </TabsTrigger>
                            <TabsTrigger 
                              value="diff"
                              className="data-[state=active]:bg-[#1E293B] data-[state=active]:text-white"
                            >
                              <GitCompare className="w-4 h-4 mr-1.5" />
                              Diff
                              {versionDiff?.diff?.stats && (
                                <span className="ml-2 text-[10px]">
                                  <span className="text-green-400">+{versionDiff.diff.stats.added_lines}</span>
                                  {' / '}
                                  <span className="text-red-400">-{versionDiff.diff.stats.removed_lines}</span>
                                </span>
                              )}
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="content" className="flex-1 overflow-hidden m-0">
                            <ScrollArea className="h-[60vh]">
                              {loadingContent ? (
                                <div className="p-4 text-center text-[#64748B]">Loading...</div>
                              ) : versionContent ? (
                                <div className="space-y-4 p-4">
                                  <div>
                                    <Label className="text-xs text-[#64748B]">Models</Label>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {versionContent.primary_models?.map((m, i) => (
                                        <Badge key={i} variant="outline" className="text-[10px] font-mono">
                                          {m.provider}/{m.model_name}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-[#64748B]">Variables</Label>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {versionContent.variables?.map(v => (
                                        <Badge key={v} variant="outline" className="text-[10px] font-mono text-[#0EA5E9]">
                                          {`{${v}}`}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-[#64748B]">Prompt Content</Label>
                                    <ScrollArea className="h-[40vh] mt-2">
                                      <pre className="p-3 bg-[#030712] border border-[#1E293B] rounded-sm text-sm font-mono text-[#F1F5F9] whitespace-pre-wrap">
                                        {versionContent.content}
                                      </pre>
                                    </ScrollArea>
                                  </div>
                                </div>
                              ) : null}
                            </ScrollArea>
                          </TabsContent>

                          <TabsContent value="diff" className="flex-1 overflow-hidden m-0">
                            <ScrollArea className="h-[60vh]">
                              {loadingDiff ? (
                                <div className="p-4 text-center text-[#64748B]">Loading diff...</div>
                              ) : versionDiff ? (
                                <div className="p-4 space-y-4">
                                  {!versionDiff.has_previous ? (
                                    <div className="text-center py-8">
                                      <GitCompare className="w-8 h-8 mx-auto text-[#64748B] mb-2" />
                                      <p className="text-sm text-[#64748B]">This is the initial version</p>
                                      <p className="text-xs text-[#64748B]">No previous version to compare</p>
                                    </div>
                                  ) : (
                                    <>
                                      {/* Diff Stats */}
                                      <div className="flex items-center gap-4 p-3 bg-[#1E293B]/50 rounded-md">
                                        <div className="flex items-center gap-2">
                                          <Plus className="w-4 h-4 text-green-400" />
                                          <span className="text-sm text-green-400">
                                            {versionDiff.diff.stats.added_lines} added
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Minus className="w-4 h-4 text-red-400" />
                                          <span className="text-sm text-red-400">
                                            {versionDiff.diff.stats.removed_lines} removed
                                          </span>
                                        </div>
                                        <span className="text-xs text-[#64748B]">
                                          Comparing v{versionDiff.previous_version} → v{versionDiff.current_version}
                                        </span>
                                      </div>

                                      {/* Visual Diff View */}
                                      <div className="border border-[#1E293B] rounded-md overflow-hidden">
                                        <div className="bg-[#030712] p-2 border-b border-[#1E293B] flex items-center justify-between">
                                          <span className="text-xs text-[#64748B]">Changes</span>
                                          <div className="flex items-center gap-2 text-[10px]">
                                            <span className="text-[#64748B]">v{versionDiff.previous_version}</span>
                                            <span className="text-[#64748B]">→</span>
                                            <span className="text-[#0EA5E9]">v{versionDiff.current_version}</span>
                                          </div>
                                        </div>
                                        <div className="max-h-[45vh] overflow-auto">
                                          <ReactDiffViewer
                                            oldValue={versionDiff.previous_content || ''}
                                            newValue={versionDiff.current_content || ''}
                                            splitView={false}
                                            useDarkTheme={true}
                                            styles={diffViewerStyles}
                                            leftTitle={`Version ${versionDiff.previous_version}`}
                                            rightTitle={`Version ${versionDiff.current_version}`}
                                            showDiffOnly={false}
                                            extraLinesSurroundingDiff={3}
                                          />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : null}
                            </ScrollArea>
                          </TabsContent>
                        </Tabs>
                      </DialogContent>
                    </Dialog>

                    {version.version !== currentVersion && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRestore(version.version)}
                          className="h-6 px-2 text-[10px] text-[#94A3B8] hover:text-white"
                          data-testid={`restore-version-${version.version}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Restore
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteVersion(version.version)}
                          disabled={deleting === version.version}
                          className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-version-${version.version}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          {deleting === version.version ? '...' : 'Delete'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function Label({ className, children }) {
  return <div className={cn("text-xs text-[#64748B]", className)}>{children}</div>;
}
